(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class BpcProductViewer extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.index = 0;
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.pointers = new Map();
      this.gestureStart = null;
      this.moved = false;
      this.lastTap = 0;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const manifest = this.querySelector('[data-bpc-viewer-manifest]');
      try {
        this.items = manifest ? JSON.parse(manifest.textContent) : [];
      } catch (error) {
        console.error('Best Prints Co. viewer manifest could not be parsed.', error);
        this.items = [];
      }

      this.dialog = this.querySelector('[data-bpc-viewer-dialog]');
      this.stage = this.querySelector('[data-bpc-viewer-stage]');
      this.image = this.querySelector('[data-bpc-viewer-image]');
      this.closeButton = this.querySelector('[data-bpc-viewer-close]');
      this.prevButton = this.querySelector('[data-bpc-viewer-prev]');
      this.nextButton = this.querySelector('[data-bpc-viewer-next]');
      this.zoomButton = this.querySelector('[data-bpc-viewer-zoom]');
      this.counter = this.querySelector('[data-bpc-viewer-counter]');
      this.caption = this.querySelector('[data-bpc-viewer-caption]');
      this.liveRegion = this.querySelector('[data-bpc-viewer-live]');

      this.closeButton?.addEventListener('click', () => this.hide());
      this.prevButton?.addEventListener('click', () => this.showPrevious());
      this.nextButton?.addEventListener('click', () => this.showNext());
      this.zoomButton?.addEventListener('click', () => this.toggleZoom());

      this.addEventListener('keydown', this.onKeyDown.bind(this));
      this.stage?.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.stage?.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.stage?.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.stage?.addEventListener('pointercancel', this.onPointerUp.bind(this));

      if (!this.movedToBody) {
        this.movedToBody = true;
        document.body.appendChild(this);
      }
    }

    show(opener) {
      const mediaId = String(opener?.dataset?.mediaId || '');
      const itemIndex = this.items.findIndex((item) => String(item.id) === mediaId);

      if (itemIndex < 0) {
        const container = opener?.closest('.product-media-container');
        const inlineMedia = container?.querySelector('deferred-media, product-model');
        const poster = inlineMedia?.querySelector('.deferred-media__poster');
        if (poster) poster.click();
        return;
      }

      this.openedBy = opener;
      this.index = itemIndex;
      this.hidden = false;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      window.pauseAllMedia?.();
      this.render();

      window.requestAnimationFrame(() => {
        if (typeof trapFocus === 'function' && this.dialog) {
          trapFocus(this, this.closeButton || this.dialog);
        } else {
          (this.closeButton || this.dialog)?.focus();
        }
      });
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.hidden = true;
      document.body.classList.remove('overflow-hidden');
      this.resetTransform();
      this.pointers.clear();
      document.body.dispatchEvent(new CustomEvent('modalClosed'));

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.openedBy || null);
      } else {
        this.openedBy?.focus?.();
      }
    }

    render() {
      const item = this.items[this.index];
      if (!item || !this.image) return;

      this.resetTransform();
      this.image.removeAttribute('srcset');
      this.image.src = item.src;
      this.image.alt = item.alt || '';
      this.image.width = item.width || 1600;
      this.image.height = item.height || 1600;
      this.image.dataset.zoomSrc = item.zoomSrc || item.src;

      if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.items.length}`;
      if (this.caption) {
        this.caption.textContent = item.alt || '';
        this.caption.hidden = !item.alt;
      }
      if (this.liveRegion) {
        this.liveRegion.textContent = `Image ${this.index + 1} of ${this.items.length}`;
      }

      const oneItem = this.items.length < 2;
      if (this.prevButton) this.prevButton.hidden = oneItem;
      if (this.nextButton) this.nextButton.hidden = oneItem;

      this.preloadAdjacent();
    }

    preloadAdjacent() {
      if (this.items.length < 2) return;
      const indexes = [
        (this.index - 1 + this.items.length) % this.items.length,
        (this.index + 1) % this.items.length
      ];
      indexes.forEach((index) => {
        const source = this.items[index]?.src;
        if (!source) return;
        const image = new Image();
        image.decoding = 'async';
        image.src = source;
      });
    }

    showPrevious() {
      if (!this.items.length) return;
      this.index = (this.index - 1 + this.items.length) % this.items.length;
      this.render();
    }

    showNext() {
      if (!this.items.length) return;
      this.index = (this.index + 1) % this.items.length;
      this.render();
    }

    onKeyDown(event) {
      if (!this.hasAttribute('open')) return;
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.hide();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.showPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.showNext();
          break;
        case '+':
        case '=':
          event.preventDefault();
          this.setScale(this.scale + 0.5);
          break;
        case '-':
          event.preventDefault();
          this.setScale(this.scale - 0.5);
          break;
        case '0':
          event.preventDefault();
          this.resetTransform();
          break;
      }
    }

    onPointerDown(event) {
      if (!this.stage || !this.image) return;
      this.stage.setPointerCapture?.(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.moved = false;

      if (this.pointers.size === 1) {
        this.gestureStart = {
          x: event.clientX,
          y: event.clientY,
          translateX: this.translateX,
          translateY: this.translateY,
          scale: this.scale
        };
      } else if (this.pointers.size === 2) {
        const [a, b] = Array.from(this.pointers.values());
        this.gestureStart = {
          distance: Math.hypot(a.x - b.x, a.y - b.y),
          scale: this.scale,
          translateX: this.translateX,
          translateY: this.translateY
        };
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.gestureStart) return;
      const previous = this.pointers.get(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (Math.abs(event.clientX - previous.x) > 2 || Math.abs(event.clientY - previous.y) > 2) {
        this.moved = true;
      }

      if (this.pointers.size === 2) {
        const [a, b] = Array.from(this.pointers.values());
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const nextScale = clamp(
          this.gestureStart.scale * (distance / Math.max(this.gestureStart.distance, 1)),
          1,
          4
        );
        this.scale = nextScale;
        this.constrainTranslation();
        this.applyTransform();
        return;
      }

      if (this.pointers.size === 1 && this.scale > 1) {
        this.translateX = this.gestureStart.translateX + (event.clientX - this.gestureStart.x);
        this.translateY = this.gestureStart.translateY + (event.clientY - this.gestureStart.y);
        this.constrainTranslation();
        this.applyTransform();
      }
    }

    onPointerUp(event) {
      const start = this.gestureStart;
      const pointerCount = this.pointers.size;
      this.pointers.delete(event.pointerId);
      this.stage?.releasePointerCapture?.(event.pointerId);

      if (pointerCount === 1 && start) {
        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;

        if (this.scale === 1 && Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
          deltaX < 0 ? this.showNext() : this.showPrevious();
        } else if (!this.moved && event.pointerType === 'touch') {
          const now = Date.now();
          if (now - this.lastTap < 320) this.toggleZoom(event.clientX, event.clientY);
          this.lastTap = now;
        } else if (!this.moved && event.pointerType === 'mouse') {
          this.toggleZoom(event.clientX, event.clientY);
        }
      }

      if (this.pointers.size === 1) {
        const remaining = Array.from(this.pointers.values())[0];
        this.gestureStart = {
          x: remaining.x,
          y: remaining.y,
          translateX: this.translateX,
          translateY: this.translateY,
          scale: this.scale
        };
      } else if (this.pointers.size === 0) {
        this.gestureStart = null;
        if (this.scale <= 1.02) this.resetTransform();
      }
    }

    toggleZoom(clientX, clientY) {
      if (this.scale > 1.05) {
        this.resetTransform();
        return;
      }

      this.scale = 2.5;
      if (clientX && clientY && this.stage) {
        const rect = this.stage.getBoundingClientRect();
        const offsetX = clientX - (rect.left + rect.width / 2);
        const offsetY = clientY - (rect.top + rect.height / 2);
        this.translateX = -offsetX * 0.55;
        this.translateY = -offsetY * 0.55;
      }
      this.constrainTranslation();
      this.applyTransform();

      const zoomSource = this.image?.dataset.zoomSrc;
      if (zoomSource && this.image.src !== zoomSource) this.image.src = zoomSource;
    }

    setScale(nextScale) {
      this.scale = clamp(nextScale, 1, 4);
      if (this.scale === 1) {
        this.translateX = 0;
        this.translateY = 0;
      }
      this.constrainTranslation();
      this.applyTransform();
    }

    constrainTranslation() {
      if (!this.stage || !this.image || this.scale <= 1) {
        this.translateX = 0;
        this.translateY = 0;
        return;
      }

      const stageRect = this.stage.getBoundingClientRect();
      const imageRect = this.image.getBoundingClientRect();
      const baseWidth = imageRect.width / Math.max(this.scale, 1);
      const baseHeight = imageRect.height / Math.max(this.scale, 1);
      const maxX = Math.max(0, (baseWidth * this.scale - stageRect.width) / 2);
      const maxY = Math.max(0, (baseHeight * this.scale - stageRect.height) / 2);

      this.translateX = clamp(this.translateX, -maxX, maxX);
      this.translateY = clamp(this.translateY, -maxY, maxY);
    }

    applyTransform() {
      if (!this.image) return;
      this.image.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
      this.image.classList.toggle('is-zoomed', this.scale > 1.05);
      if (this.zoomButton) {
        this.zoomButton.setAttribute('aria-pressed', this.scale > 1.05 ? 'true' : 'false');
        this.zoomButton.setAttribute('aria-label', this.scale > 1.05 ? 'Reset zoom' : 'Zoom image');
      }
    }

    resetTransform() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.applyTransform();
    }
  }

  if (!customElements.get('product-modal')) {
    customElements.define('product-modal', BpcProductViewer);
  }
})();
